import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Collapse,
  Table,
} from 'reactstrap';

import { REQUIREMENT_SECTIONS } from '../procurementConstants';
import { formatDate, formatMonth } from '../../formatters';

// Catatan rich-text (HTML dari react-simple-wysiwyg di Planning) ditampilkan
// ter-render — sama dengan mode readOnly RichNote di Planning RequirementTab.
const RichNoteView = ({ value }) => (
  <div
    className='form-control bg-light'
    style={{ minHeight: 96, overflow: 'auto' }}
    dangerouslySetInnerHTML={{
      __html: value || '<p class="text-muted mb-0">-</p>',
    }}
  />
);

// Procurement > Main Requirement tab — VIEW ONLY.
//
// Everything here was captured during Planning and is shown read-only in
// Procurement. Same section-nav layout as Planning's RequirementTab for visual
// parity, plus a per-cargo expandable Crude/Condensate sub-table that is
// specific to Procurement.
const ProcurementRequirementView = ({ detail }) => {
  const req = detail.requirement || {};
  // Satu cargo terbuka pada satu waktu — mengikuti perilaku klik tabel Cargo
  // Requirement di Planning RequirementTab.
  const [expandedCargo, setExpandedCargo] = useState(null);
  const [openClauses, setOpenClauses] = useState({});

  // ---- Contract ------------------------------------------------------------
  const renderContract = () => (
    <Row className='g-3'>
      <Col lg={4} sm={6}>
        <Label className='form-label'>Contract Type</Label>
        <Input value={req.contractType || '-'} readOnly disabled />
      </Col>
      <Col lg={4} sm={6}>
        <Label className='form-label'>Start Period</Label>
        <Input value={formatMonth(req.startPeriod) || '-'} readOnly disabled />
      </Col>
      <Col lg={4} sm={6}>
        <Label className='form-label'>End Period</Label>
        <Input value={formatMonth(req.endPeriod) || '-'} readOnly disabled />
      </Col>
    </Row>
  );

  // ---- Cargo Requirement (expandable Crude/Condensat sub-table) --------------
  // Layout, urutan kolom, perilaku klik (satu baris terbuka), dan scrollbar
  // sub-tabel crude disamakan dengan tabel Cargo Requirement milik Planning
  // RequirementTab — hanya tanpa kolom Action (view only).
  const renderCrudeList = c => (
    <>
      <div className='d-flex align-items-center mb-2'>
        <h6 className='mb-0 flex-grow-1'>
          <i className='ri-oil-line align-bottom me-1'></i>
          Crude / Condensat
          <span className='text-muted fw-normal fs-12 ms-2'>
            {c.cargoType}
            {c.unit ? ` @ ${c.unit}` : ''}
          </span>
        </h6>
      </div>

      {/* Tinggi dibatasi ±5 baris — sisanya scroll, header tetap terlihat. */}
      <div
        className='table-responsive bg-white border rounded'
        style={{ maxHeight: 265, overflowY: 'auto' }}
      >
        <Table className='table-sm align-middle mb-0'>
          <thead
            className='table-light'
            style={{ position: 'sticky', top: 0, zIndex: 1 }}
          >
            <tr>
              <th>Crude/Condensat</th>
              <th style={{ width: '130px' }}>Max Capacity</th>
              <th style={{ width: '220px' }}>Remark Max Capacity</th>
              <th style={{ width: '110px' }} className='text-center'>
                Is Via CIB Only ?
              </th>
              <th style={{ width: '110px' }}>% Bleeding Crude (1)</th>
              <th style={{ width: '110px' }}>% Bleeding Crude (2)</th>
            </tr>
          </thead>
          <tbody>
            {(c.crudes || []).length === 0 ? (
              <tr>
                <td colSpan={6} className='text-center text-muted py-3'>
                  No Crude / Condensat added yet
                </td>
              </tr>
            ) : (
              c.crudes.map(cr => (
                <tr key={cr.id}>
                  <td>{cr.name}</td>
                  <td>{cr.maxCapacity ?? '-'}</td>
                  <td>{cr.maxCapacityRemark || '-'}</td>
                  <td className='text-center'>
                    {cr.viaCibOnly ? 'Yes' : 'No'}
                  </td>
                  <td>{cr.blendingCrude1 ?? '-'}</td>
                  <td>{cr.blendingCrude2 ?? '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </>
  );

  const renderCargo = () => (
    <>
      <Card className='border shadow-none mt-1'>
        <CardHeader className='fw-semibold bg-light'>
          Cargo Requirement
        </CardHeader>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle table-nowrap mb-0'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Cargo Type</th>
                  <th className='text-end'>Parcel Size</th>
                  <th className='text-end'>OT (%)</th>
                  <th>Incoterms</th>
                  <th>Incoterms Notes</th>
                  <th>Delivery</th>
                  <th>DDR</th>
                  <th>DDR Notes</th>
                </tr>
              </thead>
              <tbody>
                {(req.cargos || []).length === 0 ? (
                  <tr>
                    <td colSpan={9} className='text-center text-muted py-3'>
                      No cargo data
                    </td>
                  </tr>
                ) : (
                  req.cargos.map((c, idx) => {
                    const key = c.id || idx;
                    const open = expandedCargo === key;
                    return (
                      <React.Fragment key={key}>
                        <tr>
                          <td className='text-center'>
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-ghost-secondary'
                              title={open ? 'Collapse' : 'Expand'}
                              aria-expanded={open}
                              aria-label={`Toggle Crude / Condensat for ${c.cargoType}`}
                              onClick={() =>
                                setExpandedCargo(open ? null : key)
                              }
                            >
                              <i
                                className={`ri-arrow-${open ? 'down' : 'right'}-s-line fs-16`}
                              ></i>
                            </button>
                          </td>
                          <td className='fw-medium'>{c.cargoType}</td>
                          <td className='text-end'>
                            {c.volume != null && c.volume !== ''
                              ? `${c.volume} Kbbl`
                              : '-'}
                          </td>
                          <td className='text-end'>
                            {c.ot != null && c.ot !== '' ? `±${c.ot}%` : '-'}
                            {c.otOption && (
                              <small className='text-muted d-block'>
                                {c.otOption}
                              </small>
                            )}
                          </td>
                          <td>
                            {(c.incoterms || []).length
                              ? c.incoterms.map(t => (
                                  <span
                                    key={t}
                                    className='badge bg-info-subtle text-info me-1'
                                  >
                                    {t}
                                  </span>
                                ))
                              : '-'}
                          </td>
                          <td>{c.incotermsNotes || '-'}</td>
                          <td>
                            {c.plant} - {c.unit}
                          </td>
                          <td>
                            {formatDate(c.ddrFrom)} – {formatDate(c.ddrTo)}
                          </td>
                          <td>{c.ddrNotes || '-'}</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={9} className='bg-light p-3'>
                              {renderCrudeList(c)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Card className='border shadow-none mt-3'>
        <CardHeader className='fw-semibold bg-light'>
          Notes for Cargo Requirement
        </CardHeader>
        <CardBody>
          <RichNoteView value={req.cargoNotes} />
        </CardBody>
      </Card>
    </>
  );

  // ---- Clause Detail (expandable, read-only) -------------------------------
  const renderClause = () => (
    <>
      {(req.clauses || []).map(c => {
        const open = !!openClauses[c.clauseId];
        return (
          <Card className='border mb-2' key={c.clauseId}>
            <CardHeader
              role='button'
              className='d-flex align-items-center justify-content-between py-2'
              onClick={() =>
                setOpenClauses(prev => ({ ...prev, [c.clauseId]: !open }))
              }
            >
              <span className='fw-semibold'>{c.title}</span>
              <i
                className={`ri-arrow-${open ? 'up' : 'down'}-s-line fs-18`}
              ></i>
            </CardHeader>
            <Collapse isOpen={open}>
              <CardBody>
                <div className='form-check form-switch mb-3'>
                  <Input
                    type='checkbox'
                    role='switch'
                    className='form-check-input'
                    checked={!!c.mandatory}
                    disabled
                  />
                  <Label className='form-check-label'>
                    Mandatory clause for DMUT to fulfill
                  </Label>
                </div>
                <Label className='form-label'>Summary Note</Label>
                <RichNoteView value={c.summernote} />
              </CardBody>
            </Collapse>
          </Card>
        );
      })}
    </>
  );

  const sectionRenderers = {
    contract: renderContract,
    cargo: renderCargo,
    clause: renderClause,
  };

  const sectionMeta = {
    contract: {
      icon: 'ri-file-list-2-line',
      desc: 'Contract type and period (from Planning).',
    },
    cargo: {
      icon: 'ri-ship-line',
      desc: 'Cargo specification and delivery terms (from Planning).',
    },
    clause: {
      icon: 'ri-article-line',
      desc: 'Clause details (from Planning).',
    },
  };

  const scrollToSection = id =>
    document
      .getElementById(`proc-req-sec-${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div>
      <Row>
        <Col sm={12}>
          <div className='d-flex align-items-center mb-2'>
            <div className='flex-grow-1'>
              <h5 className='mb-1'>
                <i className='ri-file-list-3-line me-2'></i>
                Requirement Information
              </h5>
              <p className='text-muted mb-2 fs-12'>
                Requirement data carried over from Planning (view only).
              </p>
            </div>
          </div>
        </Col>
      </Row>

      <div className='d-flex align-items-start gap-3'>
        {/* Left section navigation (desktop only) */}
        <div className='req-nav d-none d-lg-block'>
          <div className='req-nav-title'>Sections</div>
          {REQUIREMENT_SECTIONS.map(s => (
            <button
              key={s.id}
              type='button'
              className='req-nav-link'
              onClick={() => scrollToSection(s.id)}
            >
              <i className={`${sectionMeta[s.id].icon} me-2`}></i>
              {s.label}
            </button>
          ))}
        </div>

        <div className='flex-grow-1' style={{ minWidth: 0 }}>
          <Card className='border shadow-sm'>
            <CardBody>
              {REQUIREMENT_SECTIONS.map((s, idx) => (
                <div
                  key={s.id}
                  className={idx > 0 ? 'mt-4 pt-3 border-top' : ''}
                >
                  <p
                    id={`proc-req-sec-${s.id}`}
                    className='mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                  >
                    {s.label}
                  </p>
                  <p className='text-muted mb-3 fs-12'>
                    {sectionMeta[s.id].desc}
                  </p>
                  {sectionRenderers[s.id]()}
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProcurementRequirementView;
